import React from "react";
import { Observer, observer } from "mobx-react-lite";
import { useScreenDimensions, useDevice } from "../../_layout";
import { useTranslation } from "react-i18next";
import { useState, useEffect, useRef } from "react";
import {
  StyleSheet,
  Text,
  View,
  TouchableOpacity,
  ScrollView,
  Linking,
  Animated,
  Easing,
} from "react-native";
import { Menu, MenuItem } from "react-native-material-menu";
import {
  Ionicons,
  MaterialCommunityIcons,
  MaterialIcons,
  Feather,
} from "@expo/vector-icons";
import Colors from "../../../Resources/Color";
import GlobalStyles from "../../../Resources/Styles";
import Fonts from "../../../Resources/Fonts";
import WhitePng from "../../../assets/EFORT COACH WHITE SVG.svg";
import SearchBar from "../SearchBar";
import NoTranslateText from "../NoTranslateText";
import SyncStatusIndicator from "../SyncStatusIndicator";
import ClientWeekStatusText from "../ClientWeekStatusText";
import HoverableTouchableOpacity from "../HoverableTouchableOpacity";
import { SyncStatus } from "../../../Resources/Constants";
import Tooltip from "../Tooltip";
import MediaManager from "../../Classes/MediaManager";
import { LinearGradient } from "expo-linear-gradient";
import { useDrag, useDrop } from "react-dnd";
import { getAppVersion, compareVersions } from "../../utils/version";
import {
  resolveSideMenuEnterTargetClient,
  shouldEnableSideMenuEnterSelection,
} from "./sideMenuEnterSelection";
import {
  doesSideMenuFilterRequireWeekdaySelection,
  resolveSideMenuFilterMenuCloseTransition,
  shouldApplySideMenuFilterImmediately,
  SIDE_MENU_FILTER_MENU_MODES,
} from "./sideMenuFilterMenuFlow";
import { isPendingInvitationClient } from "../../invitations/invitedClientUiGuards";
import {
  hasPendingOrAcceptingInvitationSummaryForEmail,
  resolveInviteBlockNudgeTargetClient,
} from "../../invitations/inviteBlockNudgeUtils";
import {
  resolveSideMenuClientStateWriteTarget,
  SIDE_MENU_CLIENT_STATE_WRITE_TARGET,
} from "./sideMenuClientStatePersistence";
import FloatingMenu from "../FloatingMenu";
import SvgImage from "../SvgImage";

const FreeTrialCountdown = React.memo(
  ({ trialStartTimestamp, onUpgradePress, t, isMobile }) => {
    const CountdownIndicator = React.memo(({ startTimestamp }) => {
      const [percentageFilled, setPercentageFilled] = useState(0);
      const [daysLeft, setDaysLeft] = useState(0);

      useEffect(() => {
        // No valid start date → keep defaults, no interval, no crash
        if (!startTimestamp) {
          setPercentageFilled(0);
          setDaysLeft(0);
          return;
        }

        const startDate = new Date(startTimestamp);
        const trialPeriodEndDate = new Date(startTimestamp);
        trialPeriodEndDate.setDate(trialPeriodEndDate.getDate() + 10);

        const totalDuration =
          trialPeriodEndDate.getTime() - startDate.getTime();

        // Defensive: weird dates → just mark as finished
        if (totalDuration <= 0) {
          setPercentageFilled(100);
          setDaysLeft(0);
          return;
        }

        const updatePercentage = () => {
          const now = new Date();
          const elapsed = now.getTime() - startDate.getTime();
          const remaining = trialPeriodEndDate.getTime() - now.getTime();

          const percentage = Math.min(
            Math.max((elapsed / totalDuration) * 100, 0),
            100,
          );
          const days = Math.max(
            Math.ceil(remaining / (1000 * 60 * 60 * 24)),
            0,
          );

          setPercentageFilled(percentage);
          setDaysLeft(days);
        };

        updatePercentage();
        const intervalId = setInterval(updatePercentage, 24 * 60 * 60 * 1000);

        return () => clearInterval(intervalId);
      }, [startTimestamp]);

      // Optional: hide the whole thing if there's no trial
      if (!startTimestamp) {
        return null;
      }

      return (
        <View style={{ marginBottom: 12 }}>
          <Text style={{ color: "white", marginBottom: 8 }}>
            {t(
              daysLeft === 1
                ? "freeTrialDayLeft-string"
                : "freeTrialDaysLeft-string",
              { daysLeft },
            )}
          </Text>
          <View
            style={{
              height: 8,
              width: "100%",
              backgroundColor: "#D1D5DB",
              borderRadius: 6,
              overflow: "hidden",
            }}
          >
            <View
              style={{
                height: "100%",
                backgroundColor: Colors.bubbleColor,
                width: `${percentageFilled}%`,
                borderRadius: 6,
              }}
            />
          </View>
        </View>
      );
    });

    const SeeRatesButton = React.memo(({ onPress, isMobile }) => {
      const [isHovered, setIsHovered] = useState(false);

      return (
        <TouchableOpacity
          onPress={onPress}
          style={{ borderRadius: 14 }}
          onMouseEnter={() => !isMobile && setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <View
            style={{
              backgroundColor: Colors.bubbleColor,
              borderRadius: 14,
              borderColor: Colors.bubbleColor,
              height: 40,
              justifyContent: "center",
              alignItems: "center",
              opacity: isHovered ? 0.7 : 1,
              paddingHorizontal: 10,
            }}
          >
            <Text style={{ textAlign: "center", color: Colors.whiteColor }}>
              {t("upgradePlan-string")}
            </Text>
          </View>
        </TouchableOpacity>
      );
    });

    return (
      <View>
        <CountdownIndicator startTimestamp={trialStartTimestamp} />
        <SeeRatesButton onPress={onUpgradePress} isMobile={isMobile} />
      </View>
    );
  },
);

const SideMenu = ({ coach, requestProfileLeaveAwareNavigation = null }) => {
  const { screenSize } = useScreenDimensions();
  const { isMobile } = useDevice();
  const { t, i18n } = useTranslation();
  const INVITE_NUDGE_AUTO_DISMISS_GRACE_MS = 10000;

  const [isConfigMode, setIsConfigMode] = useState(false);
  const [isAppPartsHovered, setIsAppPartsHovered] = useState(false);
  const [tempSidebarConfig, setTempSidebarConfig] = useState([]);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const runWithProfileLeaveGuard = (action) => {
    if (requestProfileLeaveAwareNavigation) {
      requestProfileLeaveAwareNavigation(action);
      return;
    }

    action();
  };

  // Get the active sidebar configuration (from coach or default)
  const getActiveSidebarConfig = () => {
    // Define allowed sidebar item IDs
    const allowedIds = [
      "CoachDashboard",
      "CoachLibrary",
      "CoachBilling",
      "CoachCompetitions",
      "CoachApplications",
      "CoachNutrition",
      "CoachSettings",
    ];

    // Start with Dashboard at the top (always present, not stored)
    const config = [{ id: "CoachDashboard", visible: true, order: 0 }];

    if (coach.sidebarConfig && coach.sidebarConfig.length > 0) {
      // Add the stored configurable items (Library, Billing, Competitions, Applications, Nutrition)
      // Filter out any Dashboard, Settings, or invalid items
      const middleItems = coach.sidebarConfig.filter(
        (item) =>
          item.id !== "CoachDashboard" &&
          item.id !== "CoachSettings" &&
          allowedIds.includes(item.id),
      );

      // Check if Library is present in the stored config
      const hasLibrary = middleItems.some((item) => item.id === "CoachLibrary");

      // If Library is missing, add it at the beginning of middle items
      if (!hasLibrary) {
        middleItems.unshift({ id: "CoachLibrary", visible: true, order: 0 });
      }

      // Ensure they're sorted by their order property
      const sortedConfig = [...middleItems].sort((a, b) => a.order - b.order);

      // Force Library to be visible (it's mandatory)
      sortedConfig.forEach((item) => {
        if (item.id === "CoachLibrary") {
          config.push({ ...item, visible: true });
        } else {
          config.push({ ...item });
        }
      });
    } else {
      // Use default middle items if no config is stored
      const defaultMiddleItems = [
        { id: "CoachLibrary", visible: true, order: 1 },
        { id: "CoachBilling", visible: true, order: 2 },
        { id: "CoachCompetitions", visible: true, order: 3 },
        { id: "CoachApplications", visible: true, order: 4 },
      ];

      // Add Nutrition if allowed
      if (coach.allowedNutrition) {
        defaultMiddleItems.push({
          id: "CoachNutrition",
          visible: true,
          order: 5,
        });
      }

      config.push(...defaultMiddleItems);
    }

    // Add Settings at the bottom (always present, not stored)
    config.push({ id: "CoachSettings", visible: true, order: config.length });

    // Update order properties to match actual positions
    config.forEach((item, idx) => {
      item.order = idx;
    });

    return config;
  };

  const ClientRow = observer(({ client, showEnterHint = false }) => {
    const isInvitationClient = isPendingInvitationClient(client);
    const writeTarget = resolveSideMenuClientStateWriteTarget(client);
    const isCheckable =
      writeTarget !== SIDE_MENU_CLIENT_STATE_WRITE_TARGET.NONE;

    const toggleIsChecked = async () => {
      if (!coach.isLoading && coach.isActive) {
        const previousChecked = client.isChecked === true;
        const nextChecked = !previousChecked;
        client.setProperty("isChecked", nextChecked);

        try {
          if (writeTarget === SIDE_MENU_CLIENT_STATE_WRITE_TARGET.USER) {
            await client.uploadData({
              is_checked: nextChecked,
            });
          } else if (
            writeTarget ===
            SIDE_MENU_CLIENT_STATE_WRITE_TARGET.PENDING_WORKSPACE
          ) {
            await client.persistPendingWorkspaceChecked(nextChecked);
          } else {
            client.setProperty("isChecked", previousChecked);
          }
        } catch (error) {
          client.setProperty("isChecked", previousChecked);
          console.warn(
            "[side-menu] failed to persist is_checked state",
            client?.id,
            error,
          );
        }
      }
    };
    const [isHovered, setIsHovered] = useState(false);
    const [isCheckHovered, setIsCheckHovered] = useState(false);
    return (
      <View
        style={{
          flexDirection: "row",
          flex: 1,
          padding: 8,
          marginHorizontal: 4,
          marginVertical: 2,
          alignItems: "center",
          borderRadius: 14,
          backgroundColor:
            client.id === coach.selectedClientId
              ? "rgba(255, 255, 255, 0.2)"
              : isHovered || showEnterHint
                ? "rgba(255, 255, 255, 0.08)"
                : "transparent",
          paddingVertical: 8,
          opacity: client.isOfflineSelectable ? 1 : 0.5,
        }}
        onMouseEnter={() => !isMobile && setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <TouchableOpacity
          onPress={toggleIsChecked}
          key={client.id}
          onMouseEnter={() =>
            !isMobile && isCheckable && setIsCheckHovered(true)
          }
          onMouseLeave={() => setIsCheckHovered(false)}
          disabled={!isCheckable}
          style={{
            marginRight: 8,
            width: 18,
            height: 18,
            borderRadius: 4,
            borderWidth: 1.5,
            borderColor: Colors.whiteColor,
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: client.isChecked
              ? Colors.whiteColor
              : "transparent",
          }}
        >
          {(client.isChecked || isCheckHovered) && (
            <MaterialIcons
              name="check"
              size={14}
              color={client.isChecked ? Colors.blackColor : Colors.whiteColor}
              style={{
                opacity: client.isChecked ? 1 : 0.7,
              }}
            />
          )}
        </TouchableOpacity>
        <TouchableOpacity
          onPress={() => {
            if (client.isOfflineSelectable) {
              runWithProfileLeaveGuard(() =>
                coach.selectClient(client.id, t, i18n),
              );
            } else {
              if (client.coach) {
                coach.setProperty("sideMenuVisible", false);
                coach.setProperty("showingOfflineAlert", true);
              }
            }
          }}
          style={{ flex: 1 }}
        >
          <View
            style={{
              alignItems: "flex-start",
              width: "100%",
            }}
          >
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                width: "100%",
              }}
            >
              <Text
                style={{
                  color: Colors.whiteColor,
                  fontSize: 14,
                  flexShrink: 1,
                  fontStyle: "normal",
                  fontWeight: "400",
                }}
                numberOfLines={1}
              >
                {client.nameForCoach} {client.surnameForCoach}
              </Text>
              {isInvitationClient && (
                <Tooltip
                  content="invitationPendingTooltip-string"
                  placement="top"
                  maxWidth={300}
                >
                  <View
                    style={{
                      flexDirection: "row",
                      alignItems: "center",
                      backgroundColor: "rgba(255, 255, 255, 0.08)",
                      paddingHorizontal: 5,
                      paddingVertical: 4,
                      borderRadius: 6,
                      marginLeft: 8,
                      borderWidth: 0.5,
                      borderColor: "rgba(255, 255, 255, 0.15)",
                      position: "relative",
                    }}
                  >
                    <Ionicons
                      name="mail-unread-outline"
                      size={12}
                      color={"rgba(255, 255, 255, 0.7)"}
                    />
                  </View>
                </Tooltip>
              )}
            </View>
            <ClientWeekStatusText
              client={client}
              style={{
                color: isInvitationClient
                  ? "rgba(255, 255, 255, 0.5)"
                  : Colors.grayColor,
                fontSize: 11,
                marginTop: 2,
              }}
              showColorForUnprepared={!isInvitationClient}
            />
          </View>
        </TouchableOpacity>

        {(showEnterHint ||
          (client.isFetched &&
            client.id != client.coach.selectedClientId &&
            client.syncStatusManager.syncStatus != SyncStatus.SYNCED)) && (
          <View
            style={{
              marginLeft: 8,
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            {showEnterHint && (
              <View
                style={{
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <MaterialCommunityIcons
                  name="keyboard-return"
                  size={12}
                  color={Colors.lightGrayColor}
                />
              </View>
            )}
            {client.isFetched &&
              client.id != client.coach.selectedClientId &&
              client.syncStatusManager.syncStatus != SyncStatus.SYNCED && (
                <View style={{ marginLeft: showEnterHint ? 6 : 0 }}>
                  <SyncStatusIndicator
                    syncStatusManager={client.syncStatusManager}
                    white={true}
                  />
                </View>
              )}
          </View>
        )}
      </View>
    );
  });

  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false);
  const [filterMenuMode, setFilterMenuMode] = useState(
    SIDE_MENU_FILTER_MENU_MODES.FILTERS,
  );
  const [isFilterIconHovered, setIsFilterIconHovered] = useState(false);
  const pendingWeekdayReopenRef = useRef(false);
  const pendingWeekdayConditionRef = useRef(null);
  const reopenWeekdayMenuRafRef = useRef(null);

  const isFilterMenuDisabled = coach.isLoading || !coach.isActive;
  const filterMenuWidth = screenSize !== "large" ? 220 : 300;

  useEffect(() => {
    return () => {
      if (reopenWeekdayMenuRafRef.current !== null) {
        cancelAnimationFrame(reopenWeekdayMenuRafRef.current);
      }
    };
  }, []);

  // const selectClient = (clientId) => {
  //   if (!coach.isLoading && coach.isActive) {
  //     coach.setProperty("showingCoachView", "ClientDashboard");
  //     if (coach.teamCoach) {
  //       coach.teamCoach.setProperty("selectedClientId", clientId);
  //       coach.teamCoach.fetchNewClient(clientId, t, i18n.language);
  //     } else {
  //       coach.setProperty("selectedClientId", clientId);
  //       coach.fetchNewClient(clientId, t, i18n.language);
  //     }

  //     if (coach.chatIsExpanded) {
  //       coach.setProperty("chatIsExpanded", false);
  //       coach.setProperty("showingChatList", false);
  //       const mediaManager = MediaManager.getInstance();
  //       mediaManager?.closeMediaPlayer();
  //     }
  //     coach.setProperty("sideMenuVisible", false);
  //   }
  // };

  const FilterMenuSelector = observer(() => {
    const filterItems = [
      { label: "all-string", value: "", icon: "list" },
      {
        label: "pendingClients-string",
        value: "Pending",
        icon: "time-outline",
      },
      {
        label: "completedClients-string",
        value: "Complete",
        icon: "checkmark-circle-outline",
      },
      {
        label: "clientsWithBlockToCreate-string",
        value: "Bloque",
        icon: "repeat-outline",
      },
      {
        label: "clientsWithWeekToUpdate-string",
        value: "Semana",
        icon: "sync-outline",
      },
      {
        label: "startsWeekIn-string",
        value: "EmpiezaSemana",
        icon: "calendar-outline",
      },
      {
        label: "endsWeekIn-string",
        value: "TerminaSemana",
        icon: "calendar-outline",
      },
    ];
    const weekdayItems = [
      { label: "monday-string", value: 0, icon: "calendar-outline" },
      { label: "tuesday-string", value: 1, icon: "calendar-outline" },
      { label: "wednesday-string", value: 2, icon: "calendar-outline" },
      { label: "thursday-string", value: 3, icon: "calendar-outline" },
      { label: "friday-string", value: 4, icon: "calendar-outline" },
      { label: "saturday-string", value: 5, icon: "calendar-outline" },
      { label: "sunday-string", value: 6, icon: "calendar-outline" },
    ];
    const items =
      filterMenuMode === SIDE_MENU_FILTER_MENU_MODES.WEEKDAYS
        ? weekdayItems
        : filterItems;
    const menuWidth =
      filterMenuMode === SIDE_MENU_FILTER_MENU_MODES.WEEKDAYS
        ? 150
        : filterMenuWidth;

    const handleFilterMenuOpenChange = (nextOpen) => {
      if (nextOpen) {
        if (isFilterMenuDisabled) {
          return;
        }
        setIsFilterMenuOpen(true);
        return;
      }

      setIsFilterMenuOpen(false);

      const closeTransition = resolveSideMenuFilterMenuCloseTransition({
        mode: filterMenuMode,
        hasPendingWeekdayReopen: pendingWeekdayReopenRef.current,
      });
      pendingWeekdayReopenRef.current = false;
      if (!closeTransition.shouldReopen) {
        pendingWeekdayConditionRef.current = null;
      }
      setFilterMenuMode(closeTransition.nextMode);

      if (closeTransition.shouldReopen && !isFilterMenuDisabled) {
        if (reopenWeekdayMenuRafRef.current !== null) {
          cancelAnimationFrame(reopenWeekdayMenuRafRef.current);
        }
        reopenWeekdayMenuRafRef.current = requestAnimationFrame(() => {
          reopenWeekdayMenuRafRef.current = null;
          setIsFilterMenuOpen(true);
        });
      }
    };

    const renderFilterIconColor = () => {
      if (
        coach.getFilterConditionSideMenu === "EmpiezaSemana" ||
        coach.getFilterConditionSideMenu === "TerminaSemana"
      ) {
        return coach.filterConditionSideMenuDay !== null
          ? Colors.bubbleColor
          : Colors.whiteColor;
      }

      return coach.getFilterConditionSideMenu === ""
        ? Colors.whiteColor
        : Colors.bubbleColor;
    };

    return (
      <FloatingMenu
        open={isFilterMenuOpen}
        onOpenChange={handleFilterMenuOpenChange}
        width={menuWidth}
        maxHeight={280}
        menuStyle={GlobalStyles.menu}
        trigger={
          <Tooltip
            content="filterAthletes-string"
            placement="top"
            delayedOpen={true}
            disabled={isFilterMenuOpen}
          >
            <View
              onMouseEnter={() => !isMobile && setIsFilterIconHovered(true)}
              onMouseLeave={() => setIsFilterIconHovered(false)}
            >
              <Ionicons
                name="filter"
                size={12}
                color={renderFilterIconColor()}
                style={{ opacity: isFilterIconHovered ? 0.8 : 1 }}
              />
            </View>
          </Tooltip>
        }
        items={items}
        renderItem={({ item, close }) => {
          if (filterMenuMode === SIDE_MENU_FILTER_MENU_MODES.WEEKDAYS) {
            const isSelected = coach.filterConditionSideMenuDay === item.value;
            return (
              <HoverableTouchableOpacity
                onPress={() => {
                  if (pendingWeekdayConditionRef.current) {
                    coach.setFilterConditionSideMenu(
                      pendingWeekdayConditionRef.current,
                    );
                  }
                  coach.setProperty("filterConditionSideMenuDay", item.value);
                  pendingWeekdayReopenRef.current = false;
                  pendingWeekdayConditionRef.current = null;
                  setFilterMenuMode(SIDE_MENU_FILTER_MENU_MODES.FILTERS);
                  close?.();
                }}
                style={[
                  styles.sideMenuFilterFloatingRow,
                  isSelected && styles.sideMenuFilterFloatingRowSelected,
                ]}
                hoverStyle={
                  !isSelected
                    ? styles.sideMenuFilterFloatingRowHover
                    : undefined
                }
              >
                <Ionicons
                  name={item.icon}
                  size={14}
                  color={isSelected ? Colors.bubbleColor : Colors.darkgrayColor}
                />
                <Text
                  style={[
                    styles.sideMenuFilterFloatingRowText,
                    isSelected && styles.sideMenuFilterFloatingRowTextSelected,
                  ]}
                >
                  {t(item.label)}
                </Text>
              </HoverableTouchableOpacity>
            );
          }

          const isSelected = coach.filterConditionSideMenu === item.value;
          return (
            <HoverableTouchableOpacity
              onPress={() => {
                const requiresWeekdaySelection =
                  doesSideMenuFilterRequireWeekdaySelection(item.value);
                pendingWeekdayReopenRef.current = requiresWeekdaySelection;
                pendingWeekdayConditionRef.current = requiresWeekdaySelection
                  ? item.value
                  : null;
                if (shouldApplySideMenuFilterImmediately(item.value)) {
                  coach.setFilterConditionSideMenu(item.value);
                }
                close?.();
              }}
              style={[
                styles.sideMenuFilterFloatingRow,
                isSelected && styles.sideMenuFilterFloatingRowSelected,
              ]}
              hoverStyle={
                !isSelected ? styles.sideMenuFilterFloatingRowHover : undefined
              }
            >
              <Ionicons
                name={item.icon}
                size={14}
                color={isSelected ? Colors.bubbleColor : Colors.darkgrayColor}
              />
              <Text
                style={[
                  styles.sideMenuFilterFloatingRowText,
                  isSelected && styles.sideMenuFilterFloatingRowTextSelected,
                ]}
              >
                {t(item.label)}
              </Text>
            </HoverableTouchableOpacity>
          );
        }}
      />
    );
  });

  const AppPartButton = observer(({ part }) => {
    const isSelected = coach.showingCoachView === part;
    const [isHovered, setIsHovered] = useState(false);
    const iconName =
      part === "CoachDashboard"
        ? "home-outline"
        : part === "CoachLibrary"
          ? "folder-outline"
          : part === "CoachBilling"
            ? "card-outline"
            : part === "CoachCompetitions"
              ? "podium-outline"
              : part === "CoachApplications"
                ? "person-outline"
                : part === "CoachNutrition"
                  ? "nutrition-outline"
                  : "settings-outline";

    const text =
      part === "CoachDashboard"
        ? "home-string"
        : part === "CoachLibrary"
          ? "library-string"
          : part === "CoachBilling"
            ? "billing-string"
            : part === "CoachCompetitions"
              ? "competitions-string"
              : part === "CoachApplications"
                ? "applications-string"
                : part === "CoachNutrition"
                  ? "nutrition-string"
                  : "settings-string";

    const action = () => {
      if (part === "CoachApplications") {
        if (
          !coach.isLoading &&
          coach.isActive &&
          !coach.isOffline &&
          !coach.teamCoach
        ) {
          runWithProfileLeaveGuard(() => {
            coach.setProperty("showingCoachView", "CoachApplications");
            coach.setProperty("sideMenuVisible", false);
            coach.unselectClient();
            if (coach.chatIsExpanded) {
              coach.setProperty("chatIsExpanded", false);
              coach.setProperty("showingChatList", false);
              const mediaManager = MediaManager.getInstance();
              mediaManager?.closeMediaPlayer();
            }
          });
        }
      } else {
        if (!coach.isLoading && coach.isActive) {
          runWithProfileLeaveGuard(() => {
            coach.setProperty("showingCoachView", part);
            if (coach.chatIsExpanded) {
              coach.setProperty("chatIsExpanded", false);
              coach.setProperty("showingChatList", false);
              const mediaManager = MediaManager.getInstance();
              mediaManager?.closeMediaPlayer();
            }
            coach.setProperty("sideMenuVisible", false);
            coach.unselectClient();
          });
        }
      }
    };
    return (
      <TouchableOpacity
        onPress={action}
        style={{
          flexDirection: "row",
          alignItems: "center",
          marginTop: part === "CoachDashboard" ? 32 : 16,
          opacity: part === "CoachDashboard" ? 1 : coach.teamCoach ? 0.5 : 1,
        }}
        onMouseEnter={() => !isMobile && setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <Ionicons
          name={iconName}
          size={18}
          color={
            isHovered
              ? "rgba(255, 255, 255, 0.9)"
              : isSelected
                ? Colors.whiteColor
                : Colors.grayColor
          }
          style={{ marginLeft: 16 }}
        />
        <View style={{ flexDirection: "row", alignItems: "center" }}>
          <Text
            style={{
              fontWeight: "400",
              fontSize: 14,
              marginLeft: 10,
              color: isHovered
                ? "rgba(255, 255, 255, 0.8)"
                : isSelected
                  ? Colors.whiteColor
                  : Colors.grayColor,
            }}
          >
            {t(text)}
          </Text>
          {part === "CoachApplications" && coach.applicationsHasUnread && (
            <View
              style={{
                width: 8,
                height: 8,
                borderRadius: 4,
                backgroundColor: Colors.lightGrayColor,
                marginLeft: 8,
              }}
            />
          )}
        </View>
      </TouchableOpacity>
    );
  });

  // const FreeTrialCountdown = React.memo(() => {
  //   // Extract and memoize the trial start date timestamp to prevent unnecessary re-renders
  //   const trialStartTimestamp = React.useMemo(() => {
  //     const startDate = coach?.trialPeriodStartDate;
  //     if (!(startDate instanceof Date) || isNaN(startDate.getTime())) {
  //       return null;
  //     }
  //     return startDate.getTime();
  //   }, [coach?.trialPeriodStartDate?.getTime()]);

  //   const CountdownIndicator = React.memo(({ startTimestamp }) => {
  //     const [percentageFilled, setPercentageFilled] = useState(0);
  //     const [daysLeft, setDaysLeft] = useState(0);

  //     useEffect(() => {
  //       // No valid start date → keep defaults, no interval, no crash
  //       if (!startTimestamp) {
  //         setPercentageFilled(0);
  //         setDaysLeft(0);
  //         return;
  //       }

  //       const startDate = new Date(startTimestamp);
  //       const trialPeriodEndDate = new Date(startTimestamp);
  //       trialPeriodEndDate.setDate(trialPeriodEndDate.getDate() + 10);

  //       const totalDuration =
  //         trialPeriodEndDate.getTime() - startDate.getTime();

  //       // Defensive: weird dates → just mark as finished
  //       if (totalDuration <= 0) {
  //         setPercentageFilled(100);
  //         setDaysLeft(0);
  //         return;
  //       }

  //       const updatePercentage = () => {
  //         const now = new Date();
  //         const elapsed = now.getTime() - startDate.getTime();
  //         const remaining = trialPeriodEndDate.getTime() - now.getTime();

  //         const percentage = Math.min(
  //           Math.max((elapsed / totalDuration) * 100, 0),
  //           100
  //         );
  //         const days = Math.max(
  //           Math.ceil(remaining / (1000 * 60 * 60 * 24)),
  //           0
  //         );

  //         setPercentageFilled(percentage);
  //         setDaysLeft(days);
  //       };

  //       updatePercentage();
  //       const intervalId = setInterval(updatePercentage, 24 * 60 * 60 * 1000);

  //       return () => clearInterval(intervalId);
  //     }, [startTimestamp]);

  //     // Optional: hide the whole thing if there’s no trial
  //     if (!startTimestamp) {
  //       return null;
  //     }

  //     return (
  //       <View style={{ marginBottom: 12 }}>
  //         <Text style={{ color: "white", marginBottom: 8 }}>
  //           {t(
  //             daysLeft === 1
  //               ? "freeTrialDayLeft-string"
  //               : "freeTrialDaysLeft-string",
  //             { daysLeft }
  //           )}
  //         </Text>
  //         <View
  //           style={{
  //             height: 8,
  //             width: "100%",
  //             backgroundColor: "#D1D5DB",
  //             borderRadius: 6,
  //             overflow: "hidden",
  //           }}
  //         >
  //           <View
  //             style={{
  //               height: "100%",
  //               backgroundColor: Colors.bubbleColor,
  //               width: `${percentageFilled}%`,
  //             }}
  //           />
  //         </View>
  //       </View>
  //     );
  //   });

  //   const SeeRatesButton = React.memo(() => {
  //     const [isHovered, setIsHovered] = useState(false);

  //     return (
  //       <TouchableOpacity
  //         onPress={() => {
  //           coach.setProperty("sideMenuVisible", false);
  //           coach.setProperty("showingStripePayment", true);
  //         }}
  //         style={{ borderRadius: 14 }}
  //         onMouseEnter={() => !isMobile && setIsHovered(true)}
  //         onMouseLeave={() => setIsHovered(false)}
  //       >
  //         <View
  //           style={{
  //             backgroundColor: Colors.bubbleColor,
  //             borderRadius: 14,
  //             borderColor: Colors.bubbleColor,
  //             height: 40,
  //             justifyContent: "center",
  //             alignItems: "center",
  //             opacity: isHovered ? 0.7 : 1,
  //             paddingHorizontal: 10,
  //           }}
  //         >
  //           <Text style={{ textAlign: "center", color: Colors.whiteColor }}>
  //             {t("upgradePlan-string")}
  //           </Text>
  //         </View>
  //       </TouchableOpacity>
  //     );
  //   });

  //   return (
  //     <View>
  //       <CountdownIndicator startTimestamp={trialStartTimestamp} />
  //       <SeeRatesButton />
  //     </View>
  //   );
  // });

  const UpgradePlanDiscount = observer(() => {
    const UpgradePlanButton = () => {
      const [isHovered, setIsHovered] = useState(false);

      return (
        <TouchableOpacity
          onPress={() => {
            coach.setProperty("sideMenuVisible", false);
            try {
              window.location.assign(
                "https://billing.stripe.com/p/login/9AQ3fW6andcNgne000",
              );
            } catch (error) {
              console.error("Error adding document: ", error);
            }
          }}
          style={{ opacity: isHovered ? 0.7 : 1 }}
          onMouseEnter={() => !isMobile && setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <View
            style={{
              backgroundColor: Colors.bubbleColor,
              borderRadius: 14,
              justifyContent: "center",
              borderColor: Colors.bubbleColor,
              height: 40,
              justifyContent: "center",
              alignItems: "center",
              opacity: 1,
            }}
          >
            <Text
              style={{
                textAlignVertical: "center",
                textAlign: "center",
                color: Colors.whiteColor,
                marginHorizontal: 10,
              }}
            >
              {t("upgradePlan-string")}
            </Text>
          </View>
        </TouchableOpacity>
      );
    };

    const [isHovered, setIsHovered] = useState(false);

    return (
      <View>
        <View
          style={{
            width: "100%",
            alignItems: "flex-end",
          }}
        >
          <TouchableOpacity
            onPress={() => coach.setProperty("showingUpgradePlan", false)}
            onMouseEnter={() => !isMobile && setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <Ionicons
              name="close"
              size={20}
              color="gray"
              style={{
                opacity: isHovered ? 0.5 : 1,
              }}
            />
          </TouchableOpacity>
        </View>
        <Text style={{ color: "white", marginBottom: 8 }}>
          {t("youHaveMaxClients-string", {
            clientsLength: coach.clients.length,
          })}
        </Text>
        <Text style={{ color: "white", marginBottom: 16 }}>
          {t("useUpgradeDiscount-string")}
        </Text>
        <UpgradePlanButton />
      </View>
    );
  });

  const ResetClientsTextButton = observer(() => {
    const [isHovered, setIsHovered] = useState(false);
    return (
      <Tooltip
        content="resetCompletedAthletes-string"
        placement="top"
        delayedOpen={true}
        // disabled={coach.isOffline}
      >
        <TouchableOpacity
          onPress={() => {
            if (!coach.isLoading && coach.isActive) {
              if (!coach.isOffline) {
                coach.setProperty("sideMenuVisible", false);
                coach.setProperty("showingResetAlert", true);
              }
            }
          }}
          style={{ opacity: isHovered ? 0.8 : 1 }}
          onMouseEnter={() => !isMobile && setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <Text
            style={{
              color: Colors.whiteColor,
              marginRight: 12,
              opacity: coach.isOffline ? 0.5 : 1,
              fontSize: 12,
            }}
          >
            {t("reset-string")}
          </Text>
        </TouchableOpacity>
      </Tooltip>
    );
  });

  const TeamCoachMenu = () => {
    const [isMenuHovered, setIsMenuHovered] = useState(false);
    const [teamMenu, setTeamMenu] = useState(false);
    const showTeamMenu = () => setTeamMenu(true);

    const MenuItemView = ({ teamCoach, index }) => {
      const [isHovered, setIsHovered] = useState(false);

      const fetchTeamCoachProva = async () => {
        runWithProfileLeaveGuard(() => {
          coach.setProperty("showingCoachView", "CoachDashboard");
          coach.setProperty("searchTermSideMenu", "");
          coach.fetchTeamCoach(
            teamCoach.id,
            teamCoach.email,
            teamCoach.name,
            teamCoach.surname,
          );
          setTeamMenu(false);
          setIsMenuHovered(false);
        });
      };

      return index === 0 ? (
        <MenuItem
          onPress={() => {
            runWithProfileLeaveGuard(() => {
              setTeamMenu(false);
              coach.setProperty("showingCoachView", "CoachDashboard");
              coach.setProperty("searchTermSideMenu", "");
              coach.setProperty("teamCoach", null);
              setIsMenuHovered(false);
            });
          }}
          style={{ minWidth: 190 }}
          textStyle={{
            fontSize: 14,
            fontWeight: isHovered ? "600" : "normal",
          }}
          onMouseEnter={() => !isMobile && setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <View>
            <Text>
              {coach.name} {coach.surname}
            </Text>
            {!coach.isTeamCoach && (
              <Text style={{ color: Colors.grayColor, fontSize: 11 }}>
                Head Coach
              </Text>
            )}
          </View>
        </MenuItem>
      ) : (
        <MenuItem
          onPress={() => fetchTeamCoachProva()}
          style={{ minWidth: 190 }}
          textStyle={{
            fontSize: 14,
            fontWeight: isHovered ? "600" : "normal",
          }}
          onMouseEnter={() => !isMobile && setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <Text>
            {coach.name} {coach.surname}
          </Text>
        </MenuItem>
      );
    };
    return (
      coach.id === "jijijijij" && (
        <View style={{ marginHorizontal: 10, marginTop: 32 }}>
          <Menu
            visible={teamMenu}
            anchor={
              <TouchableOpacity
                onPress={showTeamMenu}
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "space-between",
                  paddingHorizontal: 10,
                  paddingVertical: 8,
                  borderRadius: 12,
                  backgroundColor: isMenuHovered
                    ? "rgba(172, 172, 172, 0.3)"
                    : "rgba(172, 172, 172, 0.2)",
                  width: "100%",
                }}
                onMouseEnter={() => !isMobile && setIsMenuHovered(true)}
                onMouseLeave={() => setIsMenuHovered(false)}
              >
                <View>
                  {coach.teamCoach ? (
                    <Text
                      style={{
                        color: "white",
                        fontSize: 14,
                        fontWeight: "500",
                      }}
                    >
                      {coach.teamCoach.name} {coach.teamCoach.surname}
                    </Text>
                  ) : (
                    <>
                      <Text
                        style={{
                          color: "white",
                          fontSize: 14,
                          fontWeight: "500",
                        }}
                      >
                        {coach.name} {coach.surname}
                      </Text>
                      <Text style={{ color: Colors.grayColor, fontSize: 11 }}>
                        Head Coach
                      </Text>
                    </>
                  )}
                </View>

                <Feather
                  name={teamMenu ? "chevron-up" : "chevron-down"}
                  size={20}
                  color="white"
                />
              </TouchableOpacity>
            }
            style={GlobalStyles.menu}
            onRequestClose={() => {
              setTeamMenu(false);
              setIsMenuHovered(false);
            }}
          >
            {coach.teamCoach && <MenuItemView index={0} />}
            {coach.teamCoaches
              .filter((c) =>
                coach.teamCoach ? coach.teamCoach.id !== c.id : true,
              )
              .map((tCoach, idx) => (
                <MenuItemView teamCoach={tCoach} index={idx + 1} key={idx} />
              ))}
          </Menu>
        </View>
      )
    );
  };

  const TutorialsButton = () => {
    const [isHovered, setIsHovered] = useState(false);

    const handleOpenDocs = () => {
      if (!coach.isOffline) {
        const isSpanish = i18n.language === "es";
        const languagePrefix = isSpanish ? "/es" : "";
        const url = `https://app.efortcoach.com/docs${languagePrefix}/program/create-block`;
        Linking.openURL(url);
      }
    };

    return (
      <TouchableOpacity
        onPress={handleOpenDocs}
        onMouseEnter={() => !isMobile && setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <View
          style={{
            position: "relative",
          }}
        >
          {/* Top gradient border */}
          <LinearGradient
            colors={
              !isHovered
                ? ["rgba(0, 0, 0, 1)", "rgba(24, 24, 24, 1)"]
                : ["rgba(24, 24, 24, 1)", "rgba(24, 24, 24, 1)"]
            }
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 0 }}
            style={{
              paddingVertical: 1,
            }}
          >
            <View
              style={{
                alignItems: "center",
                flexDirection: "row",
                flex: 1,
                paddingVertical: 6,
                paddingHorizontal: 12,
              }}
            >
              <Ionicons name="school" size={16} color={Colors.whiteColor} />
              <Text
                style={{
                  fontSize: 14,
                  fontWeight: "400",
                  marginLeft: 6,
                  color: Colors.whiteColor,
                  // background:
                  //   "linear-gradient(90deg, rgba(250, 250, 250, 1) 0%, rgba(211, 211, 211, 1) 100%)",
                  // WebkitBackgroundClip: "text",
                  // WebkitTextFillColor: "transparent",
                  // backgroundClip: "text",
                }}
              >
                {t("tutorials-string")}
              </Text>
            </View>
          </LinearGradient>
        </View>
      </TouchableOpacity>
    );
  };

  const WhatsNewButton = observer(() => {
    const [isHovered, setIsHovered] = useState(false);

    return (
      <View
        style={{
          borderTopColor: "rgba(255, 255, 255, 0.2)",
          borderTopWidth: 1,
          backgroundColor: Colors.sideMenuColor,
          paddingHorizontal: 12,
          paddingVertical: 8,
        }}
      >
        <TouchableOpacity
          onPress={() => {
            coach.setProperty("showingWhatsNew", true);
          }}
          onMouseEnter={() => !isMobile && setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          style={{
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            opacity: isHovered ? 0.8 : 1,
          }}
        >
          <Text
            style={{
              fontSize: 14,
              fontWeight: "400",
              color: Colors.lightGrayColor,
            }}
          >
            {t("whatsNew-string")}
          </Text>
          <View
            style={{
              width: 8,
              height: 8,
              borderRadius: 4,
              backgroundColor: Colors.bubbleColor,
            }}
          />
        </TouchableOpacity>
      </View>
    );
  });

  const ConfigureSidebarButton = () => {
    const [isHovered, setIsHovered] = useState(false);

    return (
      <View style={{ alignItems: "flex-start" }}>
        <TouchableOpacity
          onPress={() => {
            setIsConfigMode(true);
            // Get the current active sidebar config (which has Dashboard and Settings)
            const activeConfig = getActiveSidebarConfig();

            // Use the full config for display (including Dashboard and Settings)
            let configToEdit = activeConfig.map((item) => ({ ...item })); // Deep copy each item

            // Define the mandatory middle items that must always be present
            const mandatoryMiddleItems = [
              "CoachLibrary",
              "CoachBilling",
              "CoachCompetitions",
              "CoachApplications",
            ];

            // Find the Settings index (always last)
            const settingsIndex = configToEdit.findIndex(
              (item) => item.id === "CoachSettings",
            );

            // Check and add any missing mandatory items before Settings
            mandatoryMiddleItems.forEach((itemId) => {
              const hasItem = configToEdit.some((item) => item.id === itemId);
              if (!hasItem) {
                // Insert missing item as hidden before Settings
                configToEdit.splice(settingsIndex, 0, {
                  id: itemId,
                  visible: false,
                  order: settingsIndex,
                });
              }
            });

            // Handle Nutrition based on allowedNutrition
            if (coach.allowedNutrition) {
              // If allowed, ensure it's in the config
              const hasNutrition = configToEdit.some(
                (item) => item.id === "CoachNutrition",
              );
              if (!hasNutrition) {
                // Insert Nutrition before Settings (which is always last)
                const currentSettingsIndex = configToEdit.findIndex(
                  (item) => item.id === "CoachSettings",
                );
                configToEdit.splice(currentSettingsIndex, 0, {
                  id: "CoachNutrition",
                  visible: false,
                  order: currentSettingsIndex,
                });
              }
            } else {
              // If not allowed, remove it from the config
              configToEdit = configToEdit.filter(
                (item) => item.id !== "CoachNutrition",
              );
            }

            // Update order properties
            configToEdit.forEach((item, idx) => {
              item.order = idx;
            });

            setTempSidebarConfig(configToEdit);
          }}
          onMouseEnter={() => !isMobile && setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          style={{
            opacity: isAppPartsHovered || isMobile ? 1 : 0,
            transition: "opacity 0.2s",
            marginTop: 8,
            paddingHorizontal: 16,
          }}
        >
          <Text
            style={{
              fontSize: 10,
              fontWeight: "400",
              color: isHovered
                ? "rgba(255, 255, 255, 0.6)"
                : "rgba(255, 255, 255, 0.4)",
            }}
          >
            {t("configureSidebar-string")}
          </Text>
        </TouchableOpacity>
      </View>
    );
  };

  const SidebarConfigItem = ({ item, index }) => {
    const [isHovered, setIsHovered] = useState(false);
    const canBeHidden = ![
      "CoachLibrary",
      "CoachDashboard",
      "CoachSettings",
    ].includes(item.id); // Library, Dashboard, and Settings are mandatory
    const canBeMoved = !["CoachDashboard", "CoachSettings"].includes(item.id); // Dashboard and Settings cannot be moved
    const ref = useRef(null);

    const getSectionName = (id) => {
      switch (id) {
        case "CoachDashboard":
          return "home-string";
        case "CoachLibrary":
          return "library-string";
        case "CoachBilling":
          return "billing-string";
        case "CoachCompetitions":
          return "competitions-string";
        case "CoachApplications":
          return "applications-string";
        case "CoachNutrition":
          return "nutrition-string";
        case "CoachSettings":
          return "settings-string";
        default:
          return "";
      }
    };

    const moveItem = (dragIndex, dropIndex) => {
      const newConfig = [...tempSidebarConfig];
      const draggedItem = newConfig[dragIndex];

      // Prevent moving Dashboard (index 0) or Settings (last index)
      if (dragIndex === 0 || dragIndex === newConfig.length - 1) {
        return;
      }

      // Prevent dropping at Dashboard or Settings positions
      if (dropIndex === 0 || dropIndex === newConfig.length - 1) {
        return;
      }

      newConfig.splice(dragIndex, 1);
      newConfig.splice(dropIndex, 0, draggedItem);

      // Update order property
      newConfig.forEach((item, idx) => {
        item.order = idx;
      });

      setTempSidebarConfig(newConfig);
    };

    const [{ isOver }, drop] = useDrop({
      accept: "SIDEBAR_ITEM",
      drop: (draggedItem, monitor) => {
        if (draggedItem.index !== index) {
          moveItem(draggedItem.index, index);
        }
      },
      collect: (monitor) => ({
        isOver: monitor.isOver(),
      }),
    });

    const [{ isDragging }, drag, dragPreview] = useDrag({
      type: "SIDEBAR_ITEM",
      item: {
        type: "SIDEBAR_ITEM",
        id: item.id,
        index,
      },
      canDrag: canBeMoved,
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    });

    const toggleVisibility = () => {
      if (!canBeHidden) return;
      const newConfig = [...tempSidebarConfig];
      newConfig[index].visible = !newConfig[index].visible;
      setTempSidebarConfig(newConfig);
    };

    // Combine drag and drop refs only for movable items
    if (canBeMoved) {
      drag(drop(ref));
    }

    return (
      <View
        ref={ref}
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          paddingVertical: 4,
          paddingHorizontal: 8,
          marginHorizontal: 0,
          marginVertical: 1,
          borderRadius: 12,
          backgroundColor:
            isOver && canBeMoved
              ? "rgba(255, 255, 255, 0.15)"
              : isHovered
                ? "rgba(255, 255, 255, 0.1)"
                : "transparent",
          opacity: isDragging ? 0 : 1,
          cursor: canBeMoved ? "move" : "default",
        }}
        onMouseEnter={() => !isMobile && setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <View style={{ flexDirection: "row", alignItems: "center", flex: 1 }}>
          <MaterialIcons
            name="drag-indicator"
            size={18}
            color={canBeMoved ? Colors.grayColor : "transparent"}
            style={{ marginRight: 8 }}
          />

          <Text
            style={{
              color: item.visible ? Colors.whiteColor : Colors.grayColor,
              fontSize: 14,
              opacity: item.visible ? 1 : 0.5,
            }}
          >
            {t(getSectionName(item.id))}
          </Text>
        </View>

        <TouchableOpacity
          onPress={toggleVisibility}
          disabled={!canBeHidden}
          style={{ padding: 4 }}
        >
          <MaterialIcons
            name={item.visible ? "check-box" : "check-box-outline-blank"}
            size={20}
            color={canBeHidden ? Colors.whiteColor : Colors.grayColor}
            style={{ opacity: canBeHidden ? 1 : 0.5 }}
          />
        </TouchableOpacity>
      </View>
    );
  };

  const ConfigurationModal = () => {
    const [isSaveHovered, setIsSaveHovered] = useState(false);
    const [isCancelHovered, setIsCancelHovered] = useState(false);

    const handleSave = () => {
      // Filter out Dashboard and Settings - only save middle items
      const middleItemsToSave = tempSidebarConfig.filter(
        (item) => item.id !== "CoachDashboard" && item.id !== "CoachSettings",
      );

      // Save to coach.sidebarConfig
      coach.setProperty("sidebarConfig", [...middleItemsToSave]);
      // Persist to Firestore
      coach.uploadData({ sidebar_config: middleItemsToSave });
      setIsConfigMode(false);
      setIsAppPartsHovered(false);
    };

    const handleCancel = () => {
      // Revert to original full config (including Dashboard and Settings for display)
      const activeConfig = getActiveSidebarConfig();
      let fullConfig = activeConfig.map((item) => ({ ...item }));

      // Define the mandatory middle items that must always be present
      const mandatoryMiddleItems = [
        "CoachLibrary",
        "CoachBilling",
        "CoachCompetitions",
        "CoachApplications",
      ];

      // Find the Settings index (always last)
      const settingsIndex = fullConfig.findIndex(
        (item) => item.id === "CoachSettings",
      );

      // Check and add any missing mandatory items before Settings
      mandatoryMiddleItems.forEach((itemId) => {
        const hasItem = fullConfig.some((item) => item.id === itemId);
        if (!hasItem) {
          // Insert missing item as hidden before Settings
          fullConfig.splice(settingsIndex, 0, {
            id: itemId,
            visible: false,
            order: settingsIndex,
          });
        }
      });

      // Handle Nutrition based on allowedNutrition
      if (coach.allowedNutrition) {
        // If allowed, ensure it's in the config
        const hasNutrition = fullConfig.some(
          (item) => item.id === "CoachNutrition",
        );
        if (!hasNutrition) {
          // Insert Nutrition before Settings (which is always last)
          const currentSettingsIndex = fullConfig.findIndex(
            (item) => item.id === "CoachSettings",
          );
          fullConfig.splice(currentSettingsIndex, 0, {
            id: "CoachNutrition",
            visible: false,
            order: currentSettingsIndex,
          });
        }
      } else {
        // If not allowed, remove it from the config
        fullConfig = fullConfig.filter((item) => item.id !== "CoachNutrition");
      }

      // Update order properties
      fullConfig.forEach((item, idx) => {
        item.order = idx;
      });

      setTempSidebarConfig([...fullConfig]);
      setIsConfigMode(false);
      setIsAppPartsHovered(false);
    };

    return (
      <View
        style={{
          position: "absolute",
          top: 50,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "transparent",
          zIndex: 1000,
          flexDirection: "row",
          padding: 12,
          paddingTop: 24,
          backgroundColor: "#1E1E1E",
        }}
      >
        <View
          style={{
            flex: 1,
            width: "100%",
          }}
        >
          <View
            style={{
              marginBottom: 16,
            }}
          >
            <Text
              style={{
                fontSize: 14,
                fontWeight: "600",
                color: Colors.whiteColor,
                marginBottom: 6,
              }}
            >
              {t("customizeNavigation-string")}
            </Text>
            <Text
              style={{
                fontSize: 12,
                color: Colors.grayColor,
                lineHeight: 18,
              }}
            >
              {t("customizeNavigationDescription-string")}
            </Text>
          </View>

          <ScrollView
            style={{ flex: 1 }}
            contentContainerStyle={{
              flex: 1,
              justifyContent: "space-between",
            }}
            showsVerticalScrollIndicator={false}
          >
            <View style={{ width: "100%" }}>
              {tempSidebarConfig.map((item, index) => (
                <SidebarConfigItem key={item.id} item={item} index={index} />
              ))}
            </View>

            <View style={{ marginTop: 20, gap: 12 }}>
              <TouchableOpacity
                onPress={handleSave}
                onMouseEnter={() => !isMobile && setIsSaveHovered(true)}
                onMouseLeave={() => setIsSaveHovered(false)}
                style={{
                  padding: 10,
                  backgroundColor: Colors.bubbleColor,
                  borderRadius: 12,
                  alignItems: "center",
                  opacity: isSaveHovered ? 0.8 : 1,
                }}
              >
                <Text style={{ color: Colors.whiteColor, fontSize: 14 }}>
                  {t("save-string")}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={handleCancel}
                onMouseEnter={() => !isMobile && setIsCancelHovered(true)}
                onMouseLeave={() => setIsCancelHovered(false)}
                style={{
                  padding: 10,
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  borderRadius: 12,
                  alignItems: "center",
                  opacity: isCancelHovered ? 0.8 : 1,
                }}
              >
                <Text style={{ color: Colors.whiteColor, fontSize: 14 }}>
                  {t("cancel-string")}
                </Text>
              </TouchableOpacity>
            </View>
          </ScrollView>
        </View>
      </View>
    );
  };

  const InviteBlockNudge = observer(() => {
    const targetInvitationEmail = String(
      coach?.inviteBlockNudgeTargetEmail ?? "",
    ).trim();
    const targetClient = resolveInviteBlockNudgeTargetClient({
      clients: coach?.clients,
      invitationEmail: targetInvitationEmail,
    });
    const hasPendingSummaryForTarget =
      hasPendingOrAcceptingInvitationSummaryForEmail({
        invitationSummaries: coach?.invitationSummaries,
        invitationEmail: targetInvitationEmail,
      });
    const shouldRender =
      coach?.inviteBlockNudgeVisible === true &&
      !isMobile &&
      screenSize === "large";
    const [isMounted, setIsMounted] = useState(shouldRender);
    const [isCtaHovered, setIsCtaHovered] = useState(false);
    const [isCloseHovered, setIsCloseHovered] = useState(false);
    const animatedOpacity = useRef(
      new Animated.Value(shouldRender ? 1 : 0),
    ).current;
    const animatedTranslateY = useRef(
      new Animated.Value(shouldRender ? 0 : -8),
    ).current;

    useEffect(() => {
      if (shouldRender) {
        setIsMounted(true);
        Animated.parallel([
          Animated.timing(animatedOpacity, {
            toValue: 1,
            duration: 180,
            easing: Easing.out(Easing.cubic),
            useNativeDriver: true,
          }),
          Animated.timing(animatedTranslateY, {
            toValue: 0,
            duration: 220,
            easing: Easing.out(Easing.cubic),
            useNativeDriver: true,
          }),
        ]).start();
        return;
      }

      Animated.parallel([
        Animated.timing(animatedOpacity, {
          toValue: 0,
          duration: 140,
          easing: Easing.in(Easing.cubic),
          useNativeDriver: true,
        }),
        Animated.timing(animatedTranslateY, {
          toValue: -8,
          duration: 160,
          easing: Easing.in(Easing.cubic),
          useNativeDriver: true,
        }),
      ]).start(({ finished }) => {
        if (finished) {
          setIsMounted(false);
        }
      });
    }, [animatedOpacity, animatedTranslateY, shouldRender]);

    useEffect(() => {
      if (coach?.inviteBlockNudgeVisible !== true) return;

      if (!targetInvitationEmail) {
        coach.dismissInviteBlockNudge();
        return;
      }

      if (targetClient || hasPendingSummaryForTarget) {
        return;
      }

      const nudgeTriggeredAtMs = Number(coach?.inviteBlockNudgeTriggeredAtMs);
      if (
        Number.isFinite(nudgeTriggeredAtMs) &&
        nudgeTriggeredAtMs > 0 &&
        Date.now() - nudgeTriggeredAtMs < INVITE_NUDGE_AUTO_DISMISS_GRACE_MS
      ) {
        return;
      }

      coach.dismissInviteBlockNudge();
    }, [
      coach,
      coach?.inviteBlockNudgeVisible,
      coach?.inviteBlockNudgeTriggeredAtMs,
      hasPendingSummaryForTarget,
      targetClient,
      targetInvitationEmail,
    ]);

    if (!isMounted) {
      return null;
    }

    const openTargetInvitationClient = () => {
      if (!targetClient?.id) return;
      coach.dismissInviteBlockNudge();

      if (targetClient.isOfflineSelectable) {
        runWithProfileLeaveGuard(() =>
          coach.selectClient(targetClient.id, t, i18n),
        );
      } else if (targetClient.coach) {
        coach.setProperty("sideMenuVisible", false);
        coach.setProperty("showingOfflineAlert", true);
      }
    };

    return (
      <Animated.View
        style={[
          styles.inviteBlockNudgeContainer,
          {
            opacity: animatedOpacity,
            transform: [{ translateY: animatedTranslateY }],
          },
        ]}
      >
        <View style={styles.inviteBlockNudgeHeader}>
          <View style={styles.inviteBlockNudgeIconWrapper}>
            <Ionicons name="create-outline" size={14} color={Colors.whiteColor} />
          </View>
          <TouchableOpacity
            onPress={() => coach.dismissInviteBlockNudge()}
            style={[
              styles.inviteBlockNudgeCloseButton,
              isCloseHovered && styles.inviteBlockNudgeCloseButtonHover,
            ]}
            onMouseEnter={() => !isMobile && setIsCloseHovered(true)}
            onMouseLeave={() => !isMobile && setIsCloseHovered(false)}
          >
            <Ionicons
              name="close"
              size={16}
              color={
                isCloseHovered ? Colors.whiteColor : "rgba(255, 255, 255, 0.5)"
              }
            />
          </TouchableOpacity>
        </View>

        <Text style={styles.inviteBlockNudgeTitle}>
          {t("inviteBlockNudgeTitle-string")}
        </Text>

        {!!targetInvitationEmail && (
          <View style={styles.inviteBlockNudgeEmailRow}>
            <Ionicons
              name="mail-outline"
              size={12}
              color="rgba(255, 255, 255, 0.5)"
              style={{ marginRight: 6 }}
            />
            <Text style={styles.inviteBlockNudgeEmail} numberOfLines={1}>
              {targetInvitationEmail}
            </Text>
          </View>
        )}

        <TouchableOpacity
          style={[
            styles.inviteBlockNudgeCta,
            !targetClient?.id && styles.inviteBlockNudgeCtaDisabled,
            targetClient?.id && isCtaHovered && styles.inviteBlockNudgeCtaHover,
          ]}
          onPress={openTargetInvitationClient}
          disabled={!targetClient?.id}
          onMouseEnter={() => targetClient?.id && setIsCtaHovered(true)}
          onMouseLeave={() => setIsCtaHovered(false)}
        >
          <Text style={styles.inviteBlockNudgeCtaText}>
            {t("inviteBlockNudgeCta-string")}
          </Text>
          <Ionicons
            name="arrow-forward"
            size={14}
            color={Colors.bubbleColor}
            style={{ marginLeft: 4 }}
          />
        </TouchableOpacity>
      </Animated.View>
    );
  });

  return (
    <View
      style={[
        styles.sideMenu,
        {
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: 220,
          height: "100%",
        },
      ]}
    >
      {isConfigMode && <ConfigurationModal />}

      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          marginTop: 24,
        }}
      >
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
          }}
        >
          <SvgImage source={WhitePng} width={160} style={{ marginLeft: 16 }} />
        </View>
      </View>

      <View style={{ width: "100%", flex: 1, marginTop: 2 }}>
        <View
          onMouseEnter={() => !isMobile && setIsAppPartsHovered(true)}
          onMouseLeave={() => !isMobile && setIsAppPartsHovered(false)}
        >
          <Observer>
            {() => {
              const activeConfig = getActiveSidebarConfig();
              return (
                <>
                  {/* <TeamCoachMenu /> */}

                  {activeConfig
                    .filter((item) => item.visible)
                    .filter(
                      (item) =>
                        item.id !== "CoachNutrition" || coach.allowedNutrition,
                    )
                    .map((item) => (
                      <AppPartButton key={item.id} part={item.id} />
                    ))}

                  <ConfigureSidebarButton />
                </>
              );
            }}
          </Observer>
        </View>

        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            marginTop: 20,
            marginHorizontal: 12,
          }}
        >
          <Observer>
            {() => {
              const isEnterSelectionEnabled =
                shouldEnableSideMenuEnterSelection({
                  searchTerm: coach.searchTermSideMenu,
                  isSearchFocused,
                });

              const handleSearchSubmit = () => {
                if (!isEnterSelectionEnabled) {
                  return;
                }

                const enterTargetClient = resolveSideMenuEnterTargetClient({
                  clients: coach.getFilteredClientsSideMenu(),
                  selectedClientId: coach.selectedClient()?.id ?? null,
                });

                if (!enterTargetClient?.id) {
                  return;
                }

                runWithProfileLeaveGuard(() =>
                  coach.selectClient(enterTargetClient.id, t, i18n),
                );
              };

              return (
                <SearchBar
                  text={coach.searchTermSideMenu}
                  onChangeText={(text) => coach.setSearchTermSideMenu(text)}
                  onFocus={() => setIsSearchFocused(true)}
                  onBlur={() => setIsSearchFocused(false)}
                  onSubmitEditing={handleSearchSubmit}
                  selectTextOnFocus={true}
                  dark={true}
                  placeholder={"searchClient-string"}
                />
              );
            }}
          </Observer>
        </View>
        <View
          style={{
            marginTop: 10,
            width: "100%",
          }}
        >
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              borderRadius: 2,
              borderColor: "white",
              justifyContent: "space-between",
              paddingHorizontal: 12,
              marginBottom: 4,
            }}
          >
            <Text
              style={{
                color: Colors.grayColor,
                fontWeight: "400",
                fontSize: 12,
              }}
            >
              {t("athletes-string")}
            </Text>
            <View style={GlobalStyles.HStack}>
              <ResetClientsTextButton />
              <FilterMenuSelector />
            </View>
          </View>
          <View
            style={{
              height: 0.5,
              backgroundColor: "rgba(255, 255, 255, 0.2)",
            }}
          />
        </View>
        <View style={{ flex: 1, position: "relative" }}>
          <InviteBlockNudge />
          <Observer>
            {() => {
              // Check if there's a new version
              const currentVersion = getAppVersion();
              const lastSeenVersion = coach.lastSeenVersion || "0.0.0";
              const hasNewVersion =
                compareVersions(currentVersion, lastSeenVersion) > 0;
              const filteredClients = coach
                ? coach.getFilteredClientsSideMenu()
                : [];
              const isEnterSelectionEnabled =
                shouldEnableSideMenuEnterSelection({
                  searchTerm: coach?.searchTermSideMenu,
                  isSearchFocused,
                });
              const enterTargetClient = isEnterSelectionEnabled
                ? resolveSideMenuEnterTargetClient({
                    clients: filteredClients,
                    selectedClientId: coach.selectedClient()?.id ?? null,
                  })
                : null;

              // Memoize trial start timestamp
              const trialStartTimestamp = React.useMemo(() => {
                const startDate = coach?.trialPeriodStartDate;
                if (
                  !(startDate instanceof Date) ||
                  isNaN(startDate.getTime())
                ) {
                  return null;
                }
                return startDate.getTime();
              }, [coach?.trialPeriodStartDate?.getTime()]);

              const handleUpgradePress = React.useCallback(() => {
                coach.setProperty("sideMenuVisible", false);
                coach.setProperty("showingStripePayment", true);
              }, [coach]);

              return (
                <>
                  <ScrollView
                    style={{ flex: 1 }}
                    showsVerticalScrollIndicator={false}
                  >
                    {filteredClients.map((client, index, array) => (
                      <View key={client.id}>
                        <ClientRow
                          client={client}
                          showEnterHint={enterTargetClient?.id === client.id}
                        />
                        {index !== array.length - 1 && (
                          <View
                            style={{
                              height: 1,
                              backgroundColor: "rgba(255, 255, 255, 0.2)",
                              marginHorizontal: 12,
                            }}
                          />
                        )}
                      </View>
                    ))}
                  </ScrollView>

                  {coach.shouldShowSubscriptionUpgrade ? (
                    <>
                      {coach.subscriptionStatus !== "past_due" && (
                        <>
                          <TutorialsButton />
                          <View
                            style={{
                              bottom: 0,
                              padding: 12,
                            }}
                          >
                            <FreeTrialCountdown
                              trialStartTimestamp={trialStartTimestamp}
                              onUpgradePress={handleUpgradePress}
                              t={t}
                              isMobile={isMobile}
                            />
                          </View>
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      {coach.clients.length === coach.maxClients &&
                        coach.showingUpgradePlan && (
                          <View
                            style={{
                              bottom: 0,
                              borderTopColor: "rgba(255, 255, 255, 0.2)",
                              borderTopWidth: 1,
                              backgroundColor: Colors.sideMenuColor,
                              padding: 12,
                            }}
                          >
                            <UpgradePlanDiscount />
                          </View>
                        )}
                    </>
                  )}

                  {/* What's New Button */}
                  {hasNewVersion && <WhatsNewButton />}
                </>
              );
            }}
          </Observer>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  sideMenu: {
    marginHorizontal: "auto",
    backgroundColor: "#1E1E1E",
    overflow: "visible",
    zIndex: 20,
  },
  sideMenuFilterFloatingRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingVertical: 10,
    paddingHorizontal: 10,
    borderRadius: 8,
  },
  sideMenuFilterFloatingRowHover: {
    backgroundColor: Colors.hoveredGray,
  },
  sideMenuFilterFloatingRowSelected: {
    backgroundColor: Colors.lightGrayColor,
  },
  sideMenuFilterFloatingRowText: {
    fontSize: 12,
    color: Colors.blackColor,
    fontWeight: "500",
  },
  sideMenuFilterFloatingRowTextSelected: {
    color: Colors.bubbleColor,
  },
  inviteBlockNudgeContainer: {
    position: "absolute",
    top: 12,
    right: -278,
    width: 270,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: "rgba(255, 255, 255, 0.08)",
    backgroundColor: "rgba(24, 24, 24, 0.98)",
    padding: 16,
    shadowColor: "#000",
    shadowOpacity: 0.25,
    shadowRadius: 24,
    shadowOffset: { width: 0, height: 12 },
    zIndex: 10,
  },
  inviteBlockNudgeHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  inviteBlockNudgeIconWrapper: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: "rgba(255, 255, 255, 0.08)",
    alignItems: "center",
    justifyContent: "center",
  },
  inviteBlockNudgeCloseButton: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "transparent",
  },
  inviteBlockNudgeCloseButtonHover: {
    backgroundColor: "rgba(255, 255, 255, 0.1)",
  },
  inviteBlockNudgeTitle: {
    color: Colors.whiteColor,
    fontSize: 14,
    fontWeight: "600",
    lineHeight: 20,
    marginTop: 8,
    letterSpacing: 0.2,
  },
  inviteBlockNudgeEmailRow: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 6,
  },
  inviteBlockNudgeEmail: {
    color: "rgba(255, 255, 255, 0.6)",
    fontSize: 12,
    fontWeight: "400",
    flexShrink: 1,
  },
  inviteBlockNudgeCta: {
    height: 38,
    borderRadius: 10,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: Colors.whiteColor,
    marginTop: 16,
    flexDirection: "row",
  },
  inviteBlockNudgeCtaDisabled: {
    opacity: 0.5,
  },
  inviteBlockNudgeCtaHover: {
    opacity: 0.85,
  },
  inviteBlockNudgeCtaText: {
    color: Colors.bubbleColor,
    fontSize: 13,
    fontWeight: "600",
  },
});

export default SideMenu;
